import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import DatePicker from "react-datepicker";

import countriesData from "@/data/countriesData.json";
import { Address, CountryState } from "@/@types/Address";
import { useGoogleAddressSearch } from "@/hooks/useGooglePlacesSearch";
import { initialAddress } from "@/utils/constants";
import { countryStateMapper } from "@/mappers/generic/countryStateMapper";
import { getStatesByCountryId } from "@/services/countriesService";

type ShippingAddressFormProps = {
  shipLabel: string;
  address: Address;
  setAddress: (value: React.SetStateAction<Address>) => void;
  shipDate?: Date;
  setShipDate?: (value: React.SetStateAction<Date>) => void;
  isResidential?: boolean;
};

const ShippingAddressForm = ({
  shipLabel,
  address,
  setAddress,
  shipDate,
  setShipDate,
  isResidential,
}: ShippingAddressFormProps) => {
  const id = shipLabel.replace(" ", "").toLowerCase();
  const [statesList, setStatesList] = useState<CountryState[]>([]);

  const { handleSearch } = useGoogleAddressSearch(
    address,
    setAddress,
    id,
    initialAddress,
    false
  );

  const fetchStatesByCountry = useCallback(async () => {
    const { data } = await getStatesByCountryId(
      countriesData.find((country) => country.code === address.countryCode)
        ?.id ?? 36
    );

    try {
      if (data.status) {
        if (data.data) {
          const statesData = countryStateMapper(data.data);

          setStatesList(statesData);
        } else {
          setStatesList([{ name: "N/A", code: "NA" }]);
        }
      } else {
        // openAlertBar(data.message, false);
      }
    } catch (error) {}
  }, [address.countryCode]);

  useEffect(() => {
    fetchStatesByCountry();
  }, [fetchStatesByCountry]);

  return (
    <Card className="rounded-0 mb-3">
      <CardHeader className="bg-white">
        <CardTitle className="fs-5" tag="h3">
          {shipLabel}
        </CardTitle>
      </CardHeader>
      <CardBody>
        <FormGroup>
          <Label for="searchFrom">Search for an Address</Label>
          <Input
            id={id}
            name="searchFrom"
            placeholder="Start typing to view suggesstions..."
            type="text"
            onChange={handleSearch}
          />
        </FormGroup>
        <p className="text-center">-- OR --</p>
        <FormGroup>
          <Label for="searchFrom">
            State/Region <span className="text-danger">*</span>
          </Label>
          <Select
            className="basic-single"
            classNamePrefix="select"
            isSearchable
            name="country"
            value={statesList.find((c) => c.code === address.stateCode)}
            options={statesList}
            onChange={(value: any) => {
              setAddress({
                ...address,
                state: value.label,
                stateCode: value.code,
              });
            }}
          />
        </FormGroup>
        <Row>
          <Col xs={12} lg={6}>
            <FormGroup>
              <Label for="country">
                Country <span className="text-danger">*</span>
              </Label>
              <Select
                className="basic-single"
                classNamePrefix="select"
                isSearchable
                name="country"
                value={countriesData.find(
                  (c) => c.code === address.countryCode
                )}
                options={countriesData}
                onChange={(value: any) => {
                  setAddress({
                    ...address,
                    country: value.label,
                    countryCode: value.code,
                  });
                }}
              />
            </FormGroup>
          </Col>
          <Col xs={12} lg={6}>
            <FormGroup>
              <Label for="country">
                Postal Code <span className="text-danger">*</span>
              </Label>
              <Input
                value={address.postalCode}
                onChange={({ target }) => {
                  setAddress({
                    ...address,
                    postalCode: target.value,
                  });
                }}
              />
            </FormGroup>
          </Col>
          {isResidential && (
            <Col xs={12} lg={6}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  checked={address.isResidence}
                  onChange={({ target }) => {
                    setAddress((a) => ({
                      ...a,
                      isResidence: target.checked,
                    }));
                  }}
                />{" "}
                <Label check>Residential</Label>
              </FormGroup>
            </Col>
          )}
          {shipDate && setShipDate && (
            <Col sm={6}>
              <FormGroup>
                <Label for="country">Ship Date</Label>
                <DatePicker
                  className="form-control"
                  selected={shipDate}
                  onChange={(e: any) => {
                    setShipDate(e);
                  }}
                />
              </FormGroup>
            </Col>
          )}
        </Row>
      </CardBody>
    </Card>
  );
};

export default ShippingAddressForm;
